import { FC, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, Button } from '@mui/material';

import { AuthRole } from '@libs/types';
import { EmployerUpdateDto } from '@libs/dto';
import { useAuth, useEmployer } from '@carcareers/hooks';
import { useLazyEmployerProfileQuery } from '@carcareers/store';
import { UserInput } from '@carcareers/components';
import { useDrawer, useForm } from '@carcareers/contexts';

interface EmployerUpdateFormProps {
  onSuccess?: () => void;
}

export const EmployerUpdateForm: FC<EmployerUpdateFormProps> = ({ onSuccess = () => undefined }) => {
  const { setOpen } = useDrawer();
  const navigate = useNavigate();
  const { data, setErrors } = useForm<EmployerUpdateDto>();
  const { updateEmployer } = useEmployer();
  const [fetchEmployerProfile] = useLazyEmployerProfileQuery();
  const { setSession } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setErrors([]);

    const requestData = {
      ...data,
      user: {
        ...data?.user,
        role: AuthRole.EMPLOYER,
      },
    };

    const response = await updateEmployer(requestData, { onSuccess });

    if (response?.data) {
      const updatedUser = response.data?.user;
      if (updatedUser) {
        setSession((prev) => {
          const next = {
            ...(prev ? { ...prev, user: { ...prev.user, ...updatedUser } } : { user: updatedUser }),
          };
          try {
            window.localStorage.setItem('session', JSON.stringify(next));
          } catch {}
          return next;
        });
      }
      // Refetch profile so greeting and other profile-bound UIs update immediately
      const userId = response.data.user?.id;
      if (userId) {
        fetchEmployerProfile(userId, true);
      }
      setOpen(false);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  useEffect(() => {
    if (data) {
      setErrors([]);
    }
  }, [data, setErrors]);

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  return (
    <>
      <Typography variant="h4" sx={{ mb: 2 }}>
        User Information
      </Typography>
      <UserInput disableEmail />
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
          Update
        </Button>
      </Box>
    </>
  );
};

export default EmployerUpdateForm;
